const STORAGE_KEY = 'announcement_modal_state_v1';
const STORAGE_VERSION = 1;

function getDefaultEntry() {
  return {
    dismissCount: 0,
    dismissedAt: null,
    acknowledged: false,
    acknowledgedAt: null,
    ackReason: null,
  };
}

function getDefaultState() {
  return {
    version: STORAGE_VERSION,
    campaigns: {},
  };
}

function normalizeTimestamp(value) {
  const timestamp = Number(value);
  if (!timestamp || Number.isNaN(timestamp) || timestamp <= 0) {
    return null;
  }
  return timestamp;
}

function normalizeDismissCount(value) {
  const dismissCount = Number(value);
  if (Number.isNaN(dismissCount) || dismissCount < 0) {
    return 0;
  }
  return Math.floor(dismissCount);
}

function normalizeDismissCountCap(value) {
  const dismissCountCap = Number(value);
  if (Number.isNaN(dismissCountCap) || dismissCountCap < 1) {
    return 1;
  }
  return Math.floor(dismissCountCap);
}

function normalizeDismissCooldownHours(value) {
  const dismissCooldownHours = Number(value);
  if (Number.isNaN(dismissCooldownHours) || dismissCooldownHours < 0) {
    return 0;
  }
  return dismissCooldownHours;
}

function normalizeEntry(entry) {
  return {
    dismissCount: normalizeDismissCount(entry?.dismissCount),
    dismissedAt: normalizeTimestamp(entry?.dismissedAt),
    acknowledged: Boolean(entry?.acknowledged),
    acknowledgedAt: normalizeTimestamp(entry?.acknowledgedAt),
    ackReason: typeof entry?.ackReason === 'string' ? entry.ackReason : null,
  };
}

function canUseLocalStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readState() {
  if (!canUseLocalStorage()) {
    return getDefaultState();
  }

  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);
    if (!rawValue) {
      return getDefaultState();
    }

    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return getDefaultState();
    }

    const campaigns = {};
    Object.entries(parsed.campaigns || {}).forEach(([campaignId, entry]) => {
      campaigns[campaignId] = normalizeEntry(entry);
    });

    return {
      version: STORAGE_VERSION,
      campaigns,
    };
  } catch (error) {
    console.error('Failed to read announcement modal state:', error);
    return getDefaultState();
  }
}

function writeState(state) {
  if (!canUseLocalStorage()) {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to persist announcement modal state:', error);
  }
}

export function getAnnouncementCampaignState(campaignId) {
  if (!campaignId) {
    return getDefaultEntry();
  }

  const state = readState();
  return normalizeEntry(state.campaigns[campaignId]);
}

export function shouldShowAnnouncementCampaign({
  campaignId,
  dismissCooldownHours = 24,
  dismissCountCap = 1,
  now = Date.now(),
}) {
  if (!campaignId) {
    return false;
  }

  const entry = getAnnouncementCampaignState(campaignId);
  const normalizedDismissCountCap = normalizeDismissCountCap(dismissCountCap);

  if (entry.acknowledged || entry.dismissCount >= normalizedDismissCountCap) {
    return false;
  }

  const dismissedAt = normalizeTimestamp(entry.dismissedAt);
  if (!dismissedAt) {
    return true;
  }

  const cooldownMs = normalizeDismissCooldownHours(dismissCooldownHours) * 60 * 60 * 1000;
  if (cooldownMs <= 0) {
    return true;
  }

  return now - dismissedAt >= cooldownMs;
}

export function recordAnnouncementDismiss({
  campaignId,
  dismissCountCap = 1,
  now = Date.now(),
}) {
  if (!campaignId) {
    return getDefaultEntry();
  }

  const state = readState();
  const currentEntry = normalizeEntry(state.campaigns[campaignId]);
  const nextDismissCount = currentEntry.dismissCount + 1;
  const normalizedDismissCountCap = normalizeDismissCountCap(dismissCountCap);
  const shouldAcknowledge = currentEntry.acknowledged || nextDismissCount >= normalizedDismissCountCap;

  const nextEntry = {
    ...currentEntry,
    dismissCount: nextDismissCount,
    dismissedAt: now,
    acknowledged: shouldAcknowledge,
    acknowledgedAt: shouldAcknowledge
      ? (currentEntry.acknowledgedAt || now)
      : currentEntry.acknowledgedAt,
    ackReason: shouldAcknowledge
      ? (currentEntry.ackReason || 'dismiss_cap_reached')
      : currentEntry.ackReason,
  };

  writeState({
    ...state,
    campaigns: {
      ...state.campaigns,
      [campaignId]: nextEntry,
    },
  });

  return nextEntry;
}

export function acknowledgeAnnouncementCampaign({
  campaignId,
  reason,
  now = Date.now(),
}) {
  if (!campaignId) {
    return {
      changed: false,
      entry: getDefaultEntry(),
    };
  }

  const state = readState();
  const currentEntry = normalizeEntry(state.campaigns[campaignId]);

  if (currentEntry.acknowledged) {
    return {
      changed: false,
      entry: currentEntry,
    };
  }

  const nextEntry = {
    ...currentEntry,
    acknowledged: true,
    acknowledgedAt: now,
    ackReason: reason || 'manual_acknowledged',
  };

  writeState({
    ...state,
    campaigns: {
      ...state.campaigns,
      [campaignId]: nextEntry,
    },
  });

  return {
    changed: true,
    entry: nextEntry,
  };
}
